import {
  FlexBoxImageScaleView,
  FlexBoxView,
  getFlexNode,
} from '@play-co/timestep-core/flexbox';
import { BitmapTextAlign, View, ViewOpts } from '@play-co/timestep-core/ui';
import sounds from 'src/lib/sounds';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import i18n from 'src/lib/i18n/i18n';
import StateObserver from 'src/StateObserver';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';

const assets = {
  background: 'assets/ui/shared/frames/panel_disclaimer.png',
};

type LinkButtonOpts = {
  superview: View;
  width: number;
  height: number;
  fontSize?: number;
  localeText: () => string;
  onClick: () => void | Promise<void>;
  subFeature: string;
  labelHorizontalAlign: BitmapTextAlign;
};

const labelColor = '#BF8147';

class LinkButton extends ButtonScaleViewWithText {
  constructor(opts: LinkButtonOpts) {
    super({
      ...opts,
      font: 'Body',
      textColor: labelColor,
      labelPaddingX: 0,
      labelPaddingY: 0,
      onClick: async () => {
        sounds.sfx.play('ok');
        await opts.onClick();
      },
      tracking: {
        feature: 'shop_disclaimer_link',
        $subFeature: opts.subFeature,
      },
    });

    const { labelHorizontalAlign: align } = opts;
    const labelWidth = this.label.getTextWidth();
    let x = 0;

    if (align === 'right') {
      x = this.style.width - labelWidth;
    } else if (align === 'center') {
      x = (this.style.width - labelWidth) / 2;
    }

    // Create underline
    new View({
      superview: this,
      width: labelWidth,
      height: 2,
      x,
      y: this.style.height - 5,
      backgroundColor: labelColor,
    });
  }
}

export class DisclaimerPanel extends FlexBoxView {
  constructor(opts: ViewOpts) {
    super({
      ...opts,
      flex: {
        width: opts.width ?? '100%',
        height: opts.height ?? '100%',
      },
    });

    // Create panel background
    new FlexBoxImageScaleView({
      superview: this,
      image: assets.background,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 50, right: 50 },
        vertical: { top: 50, bottom: 50 },
      },
      flex: {
        position: 'absolute',
        width: '100%',
        height: '100%',
      },
    });

    // Create content container
    const contentContainer = new FlexBoxView({
      superview: this,
      flex: {
        width: '100%',
        height: '100%',
        paddingHorizontal: 25,
        paddingBottom: 5,
        justifyContent: 'space-between',
        alignItems: 'center',
      },
    });

    const isEn = StateObserver.getLocale() === 'en';

    // Create message labels container
    const labelsContainer = new FlexBoxView({
      superview: contentContainer,
      flex: {
        flexDirection: 'column',
      },
    });

    // Create message labels
    ['1', '2'].forEach((line) => {
      const item = new LangBitmapFontTextView({
        superview: labelsContainer,
        width: isEn ? 405 : 462,
        font: 'Body',
        size: isEn ? 14 : 21,
        color: labelColor,
        wordWrap: true,
        verticalAlign: 'center',
        localeText: () => `${line}. ${i18n(`shopDisclaimer.${line}`)}`,
      });
      getFlexNode(item).height = item.getTextHeight() + 5;
    });

    // Create links container
    const linksContainer = new FlexBoxView({
      superview: contentContainer,
      flex: {
        flexDirection: 'column',
        alignItems: 'flex-end',
      },
    });

    // Create link buttons
    const links = [
      {
        label: 'shopDisclaimer.psa',
        subFeature: 'payment_services_act_link',
        onClick: async () => {
          await openPopupPromise('popupTermsPolicyDetails', {
            mode: 'kessai',
          });
        },
      },
      {
        label: 'shopDisclaimer.cta',
        subFeature: 'commercial_transaction_act_link',
        onClick: async () => {
          await openPopupPromise('popupTermsPolicyDetails', {
            mode: 'tokusyoho',
          });
        },
      },
    ];

    links.forEach(({ label, subFeature, onClick }) => {
      new LinkButton({
        superview: linksContainer,
        fontSize: isEn ? 15 : 21,
        width: 215,
        height: isEn ? 30 : 35,
        localeText: () => i18n(label),
        subFeature,
        labelHorizontalAlign: 'right',
        onClick,
      });
    });
  }
}
